import { Mail32 } from '@packages/themes/icons';
import type {
  GTMEventGlycerinBaseClick,
  GTMEventGlycerinDisplayOverlay,
} from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useId, useState, type JSX } from 'react';
import { FocusScope } from '@react-aria/focus';
import { Box } from '@packages/shared/src/components/Box/Box';
import { Accordion } from '@packages/shared/src/components/Accordion/Accordion';
import { AccordionSummary } from '@packages/shared/src/components/Accordion/AccordionSummary';
import { Stack } from '@packages/shared/src/components/Stack/Stack';
import { CloseIconButton } from '@packages/shared/src/components/CloseIconButton/CloseIconButton';
import { AccordionDetails } from '@packages/shared/src/components/Accordion/AccordionDetails';
import { setClosed } from './helpers/closed';

const transitionDurationOpening = '1.2s';
const transitionDurationClosing = '1.5s';

export type SheetProps = {
  /** Title that opens the sheet */
  title: string;
  /** Content in the opened sheet */
  children: JSX.Element;
  /** Function that is called when the user clicks the X Button */
  onClickClose: () => void;
};

/**
 * A small sheet on the bottom screen that opens when you click on it
 */
export const Sheet = ({ title, children, onClickClose }: SheetProps) => {
  const [open, setOpen] = useState(false);
  const dispatchGTMEvent = useTracking();

  const uniqueId = useId();

  dispatchGTMEvent<GTMEventGlycerinDisplayOverlay>({
    event: 'DisplayOverlay',
    DisplayOverlayData: {
      category: 'newsletter-sheet',
      label: 'show',
    },
  });

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        overflow: 'hidden',
        width: '100%',
        zIndex: 'drawer',
      }}
    >
      {/* Curtain */}
      <Box
        role="presentation"
        sx={{
          position: 'fixed',
          inset: '0 0 0 0',
          margin: 0,
          padding: 0,
          backgroundColor: 'common.black',
          opacity: 0,
          pointerEvents: 'none',
          transition: `opacity ${transitionDurationClosing} ease-out`,
          ...(open && {
            transition: `opacity ${transitionDurationOpening} ease-in`,
            pointerEvents: 'all',
            opacity: 0.6,
          }),
        }}
        onClick={() => setOpen(false)}
      />

      <FocusScope contain={open} restoreFocus>
        <Accordion
          expanded={open}
          onChange={(_e, isExpanded) => setOpen(isExpanded)}
          sx={{
            backgroundColor: 'transparent',
            '.MuiAccordionSummary-content': { margin: 0 },
          }}
          disableGutters
          {...(open && {
            // As this accordion behaves like a modal, additional attributes are needed:
            // https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
            role: 'dialog',
            'aria-modal': 'true',
            'aria-labelledby': `${uniqueId}-heading`,
            onKeyDown: (e) => {
              if (e.key === 'Escape') {
                // close dialog if ESC is pressed
                setOpen(false);
              }
            },
          })}
        >
          {/* Sheet Header */}
          <AccordionSummary
            expandIcon={null}
            sx={{
              borderBottom: '0.125rem solid',
              borderColor: { xs: 'grey.main', md: 'primary.main' },
              minHeight: 'unset', // Mui's minHeight is really not useful here
              padding: 0, // Mui's padding is really not useful here
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing="1rem"
              sx={{
                margin: 'auto',
                cursor: 'pointer',
                width: { xs: 1, md: '27rem' },
                color: { xs: 'text.dark', md: 'primary.contrastText' },
                backgroundColor: { xs: 'grey.light', md: 'primary.main' },
                borderColor: { xs: 'primary.main', md: 'transparent' },
                borderTop: '0.125rem solid',
                paddingY: 0,
                paddingRight: 1.25,
                paddingLeft: 2.126,
              }}
            >
              <Mail32 />
              <Box sx={{ width: 1, textAlign: 'left', paddingY: 1 }} id={`${uniqueId}-heading`}>
                {title}
              </Box>
              <CloseIconButton
                sx={{ color: { xs: 'text.dark', md: 'primary.contrastText' } }}
                onClick={(e) => {
                  onClickClose();
                  setClosed();
                  dispatchGTMEvent<GTMEventGlycerinBaseClick>({
                    event: 'BaseClick',
                    BaseClickData: {
                      category: 'newsletter-sheet',
                      label: 'canceled',
                    },
                  });
                  e.stopPropagation();
                }}
              />
            </Stack>
          </AccordionSummary>

          {/* Content */}
          <AccordionDetails
            sx={{
              backgroundColor: 'grey.light',
              padding: 0,
            }}
          >
            {children}
          </AccordionDetails>
        </Accordion>
      </FocusScope>
    </Box>
  );
};
